import Vue from 'vue';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import {
    fetchProductLines,
    getFeedbackUnifiedLoginEnabled,
    setFlagForType,
    setProductLineMetadata,
    isMetadataEnabledForAnyProductLine,
    useAdoptParityCustomRolesEnabled,
} from './product-lines.utils';
import {
    setActiveProductLinesMap,
    activeSubHasPurchasedProductLine,
    getActiveSubProductLine,
    convertProductLinesArrayToMap,
} from '@/data/flags/product-lines';

export default {
    namespaced: true,
    state: {
        map: {},
        error: {},
        fetching: false,
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
            setActiveProductLinesMap(map);
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setPropertyForType (state, { type, property, value }) {
            Vue.set(state.map[type], property, value);
            setActiveProductLinesMap(state.map);
        },
        updateProductLineMap (state, { productLine }) {
            state.map[productLine.type] = productLine;
            setActiveProductLinesMap(state.map);
        },
    },
    actions: {
        async hydrate ({ dispatch, commit, getters }, { productLines } = {}) {
            if (!productLines) {
                await dispatch('loadAll', { reFetch: true });

                return;
            }

            commit('setMap', { map: convertProductLinesArrayToMap(productLines) });
            if (getters.activeHasOIDC) {
                await dispatch('subscription/fetchOIDC', '', { root: true });
            }
        },
        async loadAll ({ commit, state }, { reFetch = false } = {}) {
            if (!isEmpty(state.map) && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });

            try {
                const productLines = await fetchProductLines();
                commit('setFetching', { fetching: false });
                commit('setMap', { map: convertProductLinesArrayToMap(productLines) });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async setFlagForType ({ commit, dispatch }, { type, flag, set } = {}) {
            commit('setFetching', { fetching: true });

            try {
                await setFlagForType({ type, flag, set });
                await dispatch('loadAll', { reFetch: true });
                commit('setFetching', { fetching: false });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async setProductLineMetadata ({ commit }, { id: subId, type, data } = {}) {
            commit('setFetching', { fetching: true });

            try {
                const productLine = await setProductLineMetadata({ type, data, subId });

                commit('setFetching', { fetching: false });
                commit('updateProductLineMap', { productLine });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        updateMap ({ commit, getters, dispatch }, { productLine }) {
            commit('setFetching', { fetching: true });
            commit('updateProductLineMap', { productLine });
            dispatch('subscription/updateProductLines', { productLines: getters.list }, { root: true });
            commit('setFetching', { fetching: false });
        },
    },
    getters: {
        list: (state) => values(state.map),
        productLineByType: () => (type) => getActiveSubProductLine(type),
        hasProductLinePurchased: () => (type, metadataName = null) => {
            return activeSubHasPurchasedProductLine(type, metadataName);
        },
        areProductLinesPurchased: (state, getters) => (productLines) => {
            if (!productLines?.length) {
                return true;
            }

            return productLines.every((productLine) => {
                if (Array.isArray(productLine)) {
                    return getters.hasProductLinePurchased(...productLine);
                }

                return getters.hasProductLinePurchased(productLine);
            });
        },
        classicFeedbackEnabled: (state, getters) => {
            const feedbackProductLine = getters.productLineByType('feedback');

            if (!feedbackProductLine?.purchased) return false;

            const isUnifiedLoginEnabled = get(feedbackProductLine, 'metadata.unifiedLoginEnabled', false);

            return feedbackProductLine.configured || isUnifiedLoginEnabled;
        },
        feedbackUnifiedLoginEnabled: (state, getters) => {
            const feedbackProductLine = getters.productLineByType('feedback');

            return getFeedbackUnifiedLoginEnabled(feedbackProductLine);
        },
        activeHasLTI: (state, getters) => {
            const adoptReseller = getters.productLineByType('adoptReseller');

            return get(adoptReseller, 'metadata.ltiSSOEnabled', false);
        },
        activeHasOIDC: (state, getters) => {
            const adoptReseller = getters.productLineByType('adoptReseller');

            return get(adoptReseller, 'metadata.oidcEnabled', false);
        },
        activeHasAeuEventsOnly: (state, getters) => {
            const adoptReseller = getters.productLineByType('adoptReseller');

            return get(adoptReseller, 'metadata.aeuEventsOnly', false);
        },
        useAdoptParityCustomRolesEnabled: (state, getters) => {
            const adoptEndUser = getters.productLineByType('adoptEndUser');
            const adopt = getters.productLineByType('adopt');

            return useAdoptParityCustomRolesEnabled(adoptEndUser, adopt);
        },
        sessionReplayRetentionDays: (state, getters) => {
            const sessionReplay = getters.productLineByType('sessionReplay');

            return get(sessionReplay, 'metadata.recordingRetentionDays', 31);
        },
        hasCrossAppEnabled: (state, getters) => {
            const guides = getters.productLineByType('guides');

            return get(guides, 'metadata.crossAppGuidesInOneUIEnabled', false);
        },
        maxPublicResourceCentersAllowed: (state, getters) => {
            const guides = getters.productLineByType('guides');

            return get(guides, 'metadata.maxPublicResourceCentersAllowed', 0);
        },
        mobileAppsForAdoptPartnersEnabled: (state, getters, rootState, rootGetters) => {
            return rootGetters['auth/hasSegmentFlag']('adoptMobile');
        },
        isFetching: (state) => state.fetching,
        isMetadataEnabledForAnyProductLine: (state) => (metadataName) => {
            return isMetadataEnabledForAnyProductLine(state.map, metadataName);
        },
        isEmailIntegrationEnabled: (state, getters, rootState, rootGetters) => {
            const hasHubspotEmailIntegration = getters.hasProductLinePurchased(
                'orchestrate',
                'hubspotEmailIntegrationEnabled',
            );
            const hasMarketoEmailIntegration = getters.hasProductLinePurchased(
                'orchestrate',
                'marketoEmailIntegrationEnabled',
            );
            const hasHubspotSegmentFlag = rootGetters['auth/hasSegmentFlag']('hubspotEmail');
            const hasMarketoSegmentFlag = rootGetters['auth/hasSegmentFlag']('marketoEmail');

            return (
                (hasHubspotSegmentFlag && hasHubspotEmailIntegration) ||
                (hasMarketoSegmentFlag && hasMarketoEmailIntegration)
            );
        },
    },
};
